import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { useAppContext } from '../../context/AppContextCompat';
import { getBottomNavItems, type NavLinkConfig } from '../../config/mobileNavigation';
import { isNativeUi } from '../../utils/nativeUi';
import { hapticLight } from '../../utils/haptics';

const NATIVE_CUSTOMER_ORDERS_TAB: NavLinkConfig = {
  id: 'orders',
  label: '訂單',
  path: '/service-history',
  icon: ClipboardList,
  activePattern: /^\/service-history/,
};

const HIDDEN_PATHS = [/^\/auth/];

/**
 * 手機底部導航欄（方案 B：取代漢堡選單，次要入口收納於「我的／更多」）
 */
const BottomNavigation: React.FC = () => {
  const location = useLocation();
  const { currentUser } = useAppContext();

  const navItems = useMemo(() => {
    const items = getBottomNavItems(currentUser?.userType);
    if (!items || currentUser?.userType !== 'customer' || !isNativeUi()) return items;
    const profileIndex = items.findIndex((item) => item.id === 'profile');
    if (profileIndex < 0) return items;
    return [
      ...items.slice(0, profileIndex),
      NATIVE_CUSTOMER_ORDERS_TAB,
      ...items.slice(profileIndex),
    ];
  }, [currentUser?.userType]);

  if (!navItems || HIDDEN_PATHS.some((pattern) => pattern.test(location.pathname))) {
    return null;
  }

  return (
    <nav
      className="fixnow-app-bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-[90] bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
      aria-label="主要導覽"
    >
      <div className="flex justify-around items-stretch h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = item.activePattern.test(location.pathname);

          return (
            <Link
              key={item.id}
              to={item.path}
              onClick={() => {
                void hapticLight();
              }}
              className={`relative flex flex-col items-center justify-center flex-1 min-h-[44px] transition-colors touch-manipulation ${
                active ? 'text-blue-600' : 'text-gray-500 active:text-gray-700'
              }`}
            >
              {active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-0.5 rounded-full bg-blue-600" />
              )}
              <Icon
                size={22}
                className={`mb-0.5 ${active ? 'stroke-[2.5]' : 'stroke-[1.75]'}`}
                aria-hidden
              />
              <span className={`text-[11px] leading-tight ${active ? 'font-semibold' : 'font-normal'}`}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;
